import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/layout/PageHeader";
import { MeasurementSilhouette } from "@/components/measurements/MeasurementSilhouette";
import { MeasurementSummaryCard } from "@/components/measurements/MeasurementSummaryCard";
import { toast } from "sonner";
import { Check, X, AlertCircle, Loader2, ArrowLeft } from "lucide-react";

interface MeasurementVersion {
  id: string;
  versionNo: number;
  status: string;
  capturedAt: string;
  capturedBy?: {
    name: string;
    publicId: string;
  };
  bustCm: number;
  waistCm: number;
  hipCm: number;
  shoulderCm: number;
  sleeveLengthCm: number;
  dressLengthCm: number;
  notes?: string;
}

export default function ReviewMeasurementPage() {
  const navigate = useNavigate();
  const params = useParams();
  const versionId = params.versionId as string;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [version, setVersion] = useState<MeasurementVersion | null>(null);
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [correctionNote, setCorrectionNote] = useState("");

  useEffect(() => {
    fetchVersion();
  }, [versionId]);

  async function fetchVersion() {
    try {
      const response = await fetch("/api/customer/measurements");
      if (!response.ok) throw new Error("Failed to fetch measurements");
      const data = await response.json();

      const found =
        data.pendingVersions?.find((v: MeasurementVersion) => v.id === versionId) ||
        data.history?.find((v: MeasurementVersion) => v.id === versionId);

      if (found) {
        setVersion(found);
      } else {
        toast.error("Measurement version not found");
        navigate("/customer/measurements");
      }
    } catch (error) {
      toast.error("Failed to load measurement");
      navigate("/customer/measurements");
    } finally {
      setLoading(false);
    }
  }

  async function handleAccept() {
    setSubmitting(true);
    try {
      const response = await fetch(
        `/api/customer/measurements/versions/${versionId}/accept`,
        { method: "POST" }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to accept measurements");
      }

      toast.success("Measurements accepted successfully!");
      navigate("/customer/measurements");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to accept");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRequestCorrection() {
    if (correctionNote.length < 5) {
      toast.error("Please provide more details about the correction needed");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `/api/customer/measurements/versions/${versionId}/request-correction`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ note: correctionNote }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to request correction");
      }

      toast.success("Correction request submitted. Our team will contact you.");
      navigate("/customer/measurements");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to submit");
    } finally {
      setSubmitting(false);
      setShowCorrectionDialog(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Review Measurements" />
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-[400px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  if (!version) {
    return null;
  }

  const isPending = version.status === "PENDING_ACCEPTANCE";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Review Measurements"
        subtitle={
          isPending
            ? "Review the measurements taken during your visit"
            : "Viewing measurement details"
        }
        actions={
          <Button variant="outline" onClick={() => navigate("/customer/measurements")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        }
      />

      {isPending && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-slate-900">Action Required</p>
                <p className="text-sm text-slate-600">
                  Please review and accept these measurements or request corrections.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <MeasurementSilhouette
          data={{
            bust: version.bustCm,
            waist: version.waistCm,
            hip: version.hipCm,
            shoulder: version.shoulderCm,
            sleeveLength: version.sleeveLengthCm,
            dressLength: version.dressLengthCm,
            unit: "cm",
          }}
        />

        <div className="space-y-6">
          <MeasurementSummaryCard
            measurements={version}
            versionNo={version.versionNo}
            capturedAt={version.capturedAt}
            status={version.status}
          />

          {version.notes && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Notes from Measurement Exec</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{version.notes}</p>
              </CardContent>
            </Card>
          )}

          {isPending && (
            <Card className="border-2 border-primary/20">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <p className="text-sm text-center text-muted-foreground">
                    Do these measurements look correct?
                  </p>
                  <div className="flex gap-3">
                    <Button
                      className="flex-1"
                      onClick={handleAccept}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Accept Measurements
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowCorrectionDialog(true)}
                      disabled={submitting}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Request Correction
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={showCorrectionDialog} onOpenChange={setShowCorrectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Correction</DialogTitle>
            <DialogDescription>
              Let us know what needs to be corrected. Our team will schedule a
              follow-up visit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note">Describe the issue</Label>
              <Textarea
                id="note"
                placeholder="e.g., The waist measurement seems too tight, please re-measure..."
                value={correctionNote}
                onChange={(e) => setCorrectionNote(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Minimum 5 characters required
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCorrectionDialog(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestCorrection}
              disabled={submitting || correctionNote.length < 5}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
